import React from 'react';
import { useSearchParams } from 'react-router-dom';
import CampaignBuilderList from './CampaignBuilderList';
import CampaignCanvas from '../canvas/CampaignCanvas';

/**
 * Router for the Builder view: the open campaign lives in the `?c=<id>` query param (and the selected
 * step in `?step=<id>`), so no `App.tsx` route change is needed — the whole builder rides on the
 * existing `/admin/campaigns` index route. No `c` → the campaign card grid; a `c` → the visual-flow
 * canvas editor (`CampaignCanvas`), which replaced the former stacked accordion editor at parity.
 */
export function CampaignBuilder(props: { canWrite: boolean }): JSX.Element {
  const { canWrite } = props;
  const [searchParams, setSearchParams] = useSearchParams();

  const cParam = searchParams.get('c');
  const campaignId = cParam && Number.isFinite(Number(cParam)) ? Number(cParam) : null;
  const stepParam = searchParams.get('step');
  const selectedStepId = stepParam && Number.isFinite(Number(stepParam)) ? Number(stepParam) : null;

  const open = React.useCallback(
    (id: number) => {
      const next = new URLSearchParams(searchParams);
      next.set('c', String(id));
      next.delete('step');
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const back = React.useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('c');
    next.delete('step');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const selectStep = React.useCallback(
    (id: number | null) => {
      const next = new URLSearchParams(searchParams);
      if (id == null) next.delete('step');
      else next.set('step', String(id));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  if (campaignId == null) {
    return <CampaignBuilderList canWrite={canWrite} onOpen={open} />;
  }

  return (
    <CampaignCanvas
      campaignId={campaignId}
      canWrite={canWrite}
      selectedStepId={selectedStepId}
      onSelectStep={selectStep}
      onBack={back}
    />
  );
}

export default CampaignBuilder;
